import { useState } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import { Button } from "@mui/material";
import StatusSnackbar from "../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../Fleet/GenericFormPopup/GenericFormPopup";
import { getId } from "../HR/UpdateEmployeePopup/UpdateEmployeePopup";

interface CompanyOption {
  id: string;
  label: string;
}

interface RegisterFormValues {
  username: string;
  password: string;
  organizationId: string;
}

const fetchOrgs = async (token: string): Promise<CompanyOption[]> => {
  const { data } = await axios.get<{ id: string; name: string }[]>(
    `${apiVariable}/displayOwnerCompanies`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data.map((o) => ({ id: o.id, label: o.name }));
};

const RegisterUserPopup: React.FC<{ onSuccess?: () => void }> = ({
  onSuccess,
}) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    status: "idle" | "success" | "error";
    message?: string;
  }>({ status: "idle" });

  const { data: orgs = [] } = useQuery({
    queryKey: ["orgs"],
    queryFn: () => fetchOrgs(token!),
    enabled: Boolean(token),
  });

  const mutation = useMutation({
    mutationFn: (data: RegisterFormValues) =>
      axios.post(
        `${apiVariable}/register`,
        {
          Username: data.username,
          Password: data.password,
          OrganizationId: getId(data.organizationId),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      ),
    onSuccess: () => {
      setSnackbar({ status: "success", message: "User registered!" });
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onSuccess?.();
    },
    onError: (err: any) => {
      const data = err.response?.data;
      let msg = "Registration failed";
      if (Array.isArray(data)) {
        msg = data.map((e: any) => e.Description || e.description).join(" • ");
      } else if (typeof data === "string") {
        msg = data;
      } else if (data?.Error) {
        msg = data.Error;
      }
      setSnackbar({ status: "error", message: msg });
    },
  });

  const handleSubmit = (vals: RegisterFormValues) => {
    mutation.mutate(vals);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Add User
      </Button>

      <GenericFormPopup
        open={open}
        title="Register New User"
        initialValues={{
          username: "",
          password: "",
          organizationId: "",
        }}
        formFields={[
          {
            name: "username",
            label: "Username",
            type: "text",
            validation: { required: "Username is required" },
          },
          {
            name: "password",
            label: "Password",
            type: "password",
            validation: { required: "Password is required" },
          },
          {
            name: "organizationId",
            label: "Organization",
            type: "combo",
            dataKey: "orgs",
            validation: { required: "Organization is required" },
          },
        ]}
        autocompleteData={{ orgs: orgs }}
        onSubmit={handleSubmit}
        onClose={() => setOpen(false)}
        loading={mutation.isPending}
      />

      <StatusSnackbar
        status={snackbar.status}
        successMessage={snackbar.message || ""}
        errorMessage={snackbar.message || ""}
        handleClose={() => setSnackbar((s) => ({ ...s, status: "idle" }))}
      />
    </>
  );
};

export default RegisterUserPopup;
